import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Copy, ExternalLink, Loader2, Plus, Save, Trash2, Eye, Sparkles,
  Briefcase, Package, User, Star, Globe, TrendingUp, Film, Award,
  CheckCircle2, Circle, DollarSign, Clock, MapPin, BadgeCheck,
  GraduationCap, Building2, Trophy, Zap, Share2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MediaUploader, isVideoUrl } from "@/components/portfolio/MediaUploader";
import { SortableGrid, DragHandle } from "@/components/portfolio/SortableGrid";

export const Route = createFileRoute("/_authenticated/portfolio")({
  head: () => ({ meta: [{ title: "My portfolio — Lumvio" }, { name: "robots", content: "noindex" }] }),
  component: PortfolioEditor,
});

// ---------- i18n helper ----------
function useT() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (en: string, arStr: string) => (ar ? arStr : en);
}

// ---------- Types ----------
type ProjectRow = {
  id: string;
  title: string;
  description: string | null;
  cover_url: string | null;
  external_url: string | null;
  tags: string[];
  role: string | null;
  tools: string[];
  outcome: string | null;
  gallery: string[];
  category: string | null;
  sort_order: number;
};
type Tier = { name: string; price: number; delivery_days: number; features: string[]; recommended?: boolean };
type ServiceRow = {
  id: string;
  title: string;
  description: string | null;
  price_from: number | null;
  delivery_days: number | null;
  tiers: Tier[];
  thumbnail_url: string | null;
  category: string | null;
  sort_order: number;
};
type TestimonialRow = { id: string; client_name: string; client_role: string | null; quote: string; rating: number | null };
type ExperienceRow = {
  id: string;
  kind: "employment" | "education" | "certification";
  title: string;
  org: string | null;
  start_date: string | null;
  end_date: string | null;
  description: string | null;
  credential_url: string | null;
  sort_order: number;
};

// ---------- Root ----------
function PortfolioEditor() {
  const { user, profile, refreshProfile } = useAuth();
  const qc = useQueryClient();
  const t = useT();

  if (!profile) return <div className="py-16 text-center text-muted-foreground">{t("Loading…", "جارٍ التحميل…")}</div>;

  if (profile.role !== "freelancer") {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Globe className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-semibold">{t("Portfolios are for freelancers", "معرض الأعمال للمستقلين فقط")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("Switch your account to a freelancer profile to publish a public portfolio.", "حوّل حسابك إلى حساب مستقل لنشر معرض أعمال عام.")}
        </p>
      </div>
    );
  }

  return <Editor userId={user!.id} qc={qc} refreshProfile={refreshProfile} />;
}

// ---------- Editor shell ----------
function Editor({
  userId, qc, refreshProfile,
}: { userId: string; qc: ReturnType<typeof useQueryClient>; refreshProfile: () => Promise<void> }) {
  const t = useT();
  const { data: prof, refetch: refetchProf } = useQuery({
    queryKey: ["my-portfolio-profile", userId],
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: projects = [] } = useQuery<ProjectRow[]>({
    queryKey: ["portfolio-projects", userId],
    queryFn: async () => {
      const { data, error } = await supabase.from("portfolio_projects").select("*")
        .eq("user_id", userId).order("sort_order").order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map(normalizeProject) as ProjectRow[];
    },
  });
  const { data: services = [] } = useQuery<ServiceRow[]>({
    queryKey: ["portfolio-services", userId],
    queryFn: async () => {
      const { data, error } = await supabase.from("portfolio_services").select("*")
        .eq("user_id", userId).order("sort_order").order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map(normalizeService) as ServiceRow[];
    },
  });
  const { data: testimonials = [] } = useQuery<TestimonialRow[]>({
    queryKey: ["portfolio-testimonials", userId],
    queryFn: async () => {
      const { data, error } = await supabase.from("portfolio_testimonials").select("*")
        .eq("user_id", userId).order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any;
    },
  });
  const { data: experience = [] } = useQuery<ExperienceRow[]>({
    queryKey: ["portfolio-experience", userId],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("portfolio_experience").select("*")
        .eq("user_id", userId).order("sort_order").order("start_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any;
    },
  });

  const publicUrl = prof?.portfolio_username
    ? `${window.location.origin}/portfolio/${prof.portfolio_username}`
    : null;

  const checklist = useMemo(() => {
    const items = [
      { id: "avatar", label: t("Add a profile photo", "أضف صورة شخصية"), done: !!prof?.avatar_url, jumpTo: "header" },
      { id: "username", label: t("Pick your public URL", "اختر رابطك العام"), done: !!prof?.portfolio_username, jumpTo: "header" },
      { id: "headline", label: t("Write a headline", "اكتب عنوانًا مهنيًا"), done: !!prof?.portfolio_headline, jumpTo: "header" },
      { id: "bio", label: t("Write your bio (40+ chars)", "اكتب نبذتك (40 حرفًا أو أكثر)"), done: (prof?.portfolio_bio ?? "").length > 40, jumpTo: "about" },
      { id: "skills", label: t("Add 3+ skills", "أضف 3 مهارات أو أكثر"), done: (prof?.portfolio_skills ?? []).length >= 3, jumpTo: "about" },
      { id: "rate", label: t("Set your hourly rate", "حدّد سعر الساعة"), done: !!prof?.portfolio_hourly_rate_usd, jumpTo: "header" },
      { id: "projects", label: t("Add 3+ portfolio items", "أضف 3 أعمال أو أكثر"), done: projects.length >= 3, jumpTo: "projects" },
      { id: "service", label: t("Publish 1+ service", "انشر خدمة واحدة على الأقل"), done: services.length >= 1, jumpTo: "services" },
      { id: "kyc", label: t("Verify your identity", "وثّق هويتك"), done: prof?.kyc_status === "verified", jumpTo: "kyc" },
    ];
    const done = items.filter((i) => i.done).length;
    return { items, pct: Math.round((done / items.length) * 100) };
  }, [prof, projects.length, services.length, t]);

  return (
    <div className="space-y-6">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-primary/10 via-card/80 to-card/60 p-6 md:p-8 shadow-[0_20px_60px_-30px_color-mix(in_oklab,var(--primary)_60%,transparent)]">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-16 bottom-0 h-40 w-40 rounded-full bg-primary/10 blur-2xl" />

        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-primary">
                <Sparkles className="h-3 w-3" /> {t("Portfolio Studio 3.0", "استوديو الأعمال 3.0")}
              </span>
              {prof?.kyc_status === "verified" && (
                <Badge variant="secondary" className="gap-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px]">
                  <BadgeCheck className="h-3 w-3" /> {t("Verified", "موثّق")}
                </Badge>
              )}
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">{t("Your public profile", "ملفك العام")}</h1>
            <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">
              {t("Build a hire-ready profile — cover, stats, portfolio, productized services, and reviews. Share one link, get hired.",
                 "ابنِ ملفًا جاهزًا للتوظيف — غلاف، إحصائيات، أعمال، خدمات مُعبّأة، وتقييمات. شارك رابطًا واحدًا واحصل على العمل.")}
            </p>

            <div className="mt-5 grid grid-cols-4 gap-2 max-w-lg">
              <MiniStat icon={<Briefcase className="h-3.5 w-3.5" />} label={t("Work", "أعمال")} value={projects.length} />
              <MiniStat icon={<Package className="h-3.5 w-3.5" />} label={t("Services", "خدمات")} value={services.length} />
              <MiniStat icon={<Star className="h-3.5 w-3.5" />} label={t("Reviews", "تقييمات")} value={testimonials.length} />
              <MiniStat icon={<Award className="h-3.5 w-3.5" />} label={t("Exp", "خبرة")} value={experience.length} />
            </div>
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            {publicUrl ? (
              <div className="flex items-center gap-1.5 rounded-full border border-border/70 bg-background/80 backdrop-blur ps-3.5 pe-1 py-1 text-sm shadow-sm">
                <Globe className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="max-w-[220px] truncate">{publicUrl.replace(/^https?:\/\//, "")}</span>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full"
                        onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success(t("Link copied", "تم نسخ الرابط")); }}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button asChild size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full">
                  <a href={publicUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                </Button>
              </div>
            ) : (
              <div className="rounded-full border border-dashed border-border/70 bg-background/40 px-3 py-1.5 text-xs text-muted-foreground">
                {t("Pick a username to activate your link", "اختر اسم مستخدم لتفعيل الرابط")}
              </div>
            )}
            {publicUrl && (
              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                <Button asChild size="sm" variant="secondary" className="rounded-full gap-1.5">
                  <Link to="/portfolio/$username" params={{ username: prof!.portfolio_username! }} target="_blank">
                    <Eye className="h-3.5 w-3.5" /> {t("Preview public page", "معاينة الصفحة العامة")}
                  </Link>
                </Button>
                <Button
                  size="sm"
                  className="rounded-full gap-1.5"
                  onClick={async () => {
                    const shareData = {
                      title: `${prof?.full_name || `@${prof?.portfolio_username}`} — Lumvio`,
                      text: t(
                        "Check out my freelancer profile on Lumvio",
                        "اطّلع على ملفي الشخصي كمستقل على Lumvio"
                      ),
                      url: publicUrl,
                    };
                    try {
                      if (typeof navigator !== "undefined" && (navigator as any).share) {
                        await (navigator as any).share(shareData);
                        return;
                      }
                    } catch { /* user cancelled — fall through to copy */ }
                    try {
                      await navigator.clipboard.writeText(publicUrl);
                      toast.success(t("Link copied — share it anywhere", "تم نسخ الرابط — شاركه في أي مكان"));
                    } catch {
                      toast.error(t("Couldn't copy the link", "تعذّر نسخ الرابط"));
                    }
                  }}
                >
                  <Share2 className="h-3.5 w-3.5" /> {t("Share profile", "شارك الملف")}
                </Button>
              </div>
            )}

            <div className="w-full md:w-64 rounded-2xl border border-border/60 bg-background/60 p-3 backdrop-blur">
              <div className="flex items-center justify-between text-[11px] font-medium">
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <TrendingUp className="h-3 w-3" /> {t("Profile strength", "قوة الملف")}
                </span>
                <span className="tabular-nums">{checklist.pct}%</span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all"
                     style={{ width: `${checklist.pct}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <StrengthChecklist items={checklist.items} pct={checklist.pct} />

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="w-full grid grid-cols-5 h-11 rounded-xl bg-muted/60 p-1">
          <TabsTrigger value="profile" className="rounded-lg gap-1.5 text-xs md:text-sm"><User className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Header", "الترويسة")}</span></TabsTrigger>
          <TabsTrigger value="projects" className="rounded-lg gap-1.5 text-xs md:text-sm"><Briefcase className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Portfolio", "الأعمال")}</span></TabsTrigger>
          <TabsTrigger value="services" className="rounded-lg gap-1.5 text-xs md:text-sm"><Package className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Services", "الخدمات")}</span></TabsTrigger>
          <TabsTrigger value="experience" className="rounded-lg gap-1.5 text-xs md:text-sm"><Award className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Experience", "الخبرة")}</span></TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-lg gap-1.5 text-xs md:text-sm"><Star className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Reviews", "التقييمات")}</span></TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfileForm userId={userId} initial={prof} onSaved={async () => { await refetchProf(); await refreshProfile(); qc.invalidateQueries({ queryKey: ["public-portfolio"] }); }} />
        </TabsContent>
        <TabsContent value="projects">
          <ProjectsEditor userId={userId} items={projects}
            onChanged={() => qc.invalidateQueries({ queryKey: ["portfolio-projects", userId] })} />
        </TabsContent>
        <TabsContent value="services">
          <ServicesEditor userId={userId} items={services}
            onChanged={() => qc.invalidateQueries({ queryKey: ["portfolio-services", userId] })} />
        </TabsContent>
        <TabsContent value="experience">
          <ExperienceEditor userId={userId} items={experience}
            onChanged={() => qc.invalidateQueries({ queryKey: ["portfolio-experience", userId] })} />
        </TabsContent>
        <TabsContent value="reviews">
          <ReviewsPanel items={testimonials} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Small UI ----------
function MiniStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border/50 bg-background/60 px-3 py-2 backdrop-blur">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">{icon} {label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
function EmptyBlock({ icon, title, body, cta }: { icon: React.ReactNode; title: string; body: string; cta?: React.ReactNode }) {
  return (
    <div className="rounded-3xl border-2 border-dashed border-border/60 bg-muted/20 p-10 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary">{icon}</div>
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}

function StrengthChecklist({ items, pct }: { items: { id: string; label: string; done: boolean; jumpTo: string }[]; pct: number }) {
  const t = useT();
  return (
    <Card className="border-border/60 bg-card/60 backdrop-blur">
      <CardContent className="p-4 md:p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold inline-flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" /> {t("Complete your profile to get discovered", "أكمل ملفك ليكتشفك العملاء")}
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">{items.filter(i => i.done).length}/{items.length} · {pct}%</span>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
          {items.map((it) => (
            <div key={it.id} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${it.done ? "border-emerald-500/20 bg-emerald-500/5 text-foreground" : "border-border/60 bg-background/40 text-muted-foreground"}`}>
              {it.done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4" />}
              <span className={it.done ? "line-through opacity-70" : ""}>{it.label}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Header/Profile form ----------
function ProfileForm({
  userId, initial, onSaved,
}: { userId: string; initial: any; onSaved: () => Promise<void> }) {
  const t = useT();
  const [username, setUsername] = useState(initial?.portfolio_username ?? "");
  const [headline, setHeadline] = useState(initial?.portfolio_headline ?? "");
  const [bio, setBio] = useState(initial?.portfolio_bio ?? "");
  const [bioLong, setBioLong] = useState(initial?.portfolio_bio_long ?? "");
  const [skillsText, setSkillsText] = useState((initial?.portfolio_skills ?? []).join(", "));
  const [cover, setCover] = useState<string | null>(initial?.portfolio_cover_url ?? null);
  const [country, setCountry] = useState(initial?.portfolio_country_code ?? "");
  const [languagesText, setLanguagesText] = useState(
    Array.isArray(initial?.portfolio_languages)
      ? initial.portfolio_languages.map((l: any) => (typeof l === "string" ? l : l.code || l.name || "")).filter(Boolean).join(", ")
      : "",
  );
  const [hourly, setHourly] = useState<string>(initial?.portfolio_hourly_rate_usd ? String(initial.portfolio_hourly_rate_usd) : "");
  const [starting, setStarting] = useState<string>(initial?.portfolio_starting_price_usd ? String(initial.portfolio_starting_price_usd) : "");
  const [availability, setAvailability] = useState<string>(initial?.portfolio_availability ?? "open");
  const [responseHours, setResponseHours] = useState<string>(initial?.portfolio_response_hours ? String(initial.portfolio_response_hours) : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!initial) return;
    setUsername(initial.portfolio_username ?? "");
    setHeadline(initial.portfolio_headline ?? "");
    setBio(initial.portfolio_bio ?? "");
    setBioLong(initial.portfolio_bio_long ?? "");
    setSkillsText((initial.portfolio_skills ?? []).join(", "));
    setCover(initial.portfolio_cover_url ?? null);
    setCountry(initial.portfolio_country_code ?? "");
    setLanguagesText(Array.isArray(initial.portfolio_languages)
      ? initial.portfolio_languages.map((l: any) => (typeof l === "string" ? l : l.code || l.name || "")).filter(Boolean).join(", ")
      : "");
    setHourly(initial.portfolio_hourly_rate_usd ? String(initial.portfolio_hourly_rate_usd) : "");
    setStarting(initial.portfolio_starting_price_usd ? String(initial.portfolio_starting_price_usd) : "");
    setAvailability(initial.portfolio_availability ?? "open");
    setResponseHours(initial.portfolio_response_hours ? String(initial.portfolio_response_hours) : "");
  }, [initial]);

  const usernameValid = useMemo(() => /^[a-z0-9_-]{3,32}$/.test(username), [username]);
  const [availability_, setAvailability_] = useState<"idle" | "checking" | "free" | "taken">("idle");

  useEffect(() => {
    const current = (initial?.portfolio_username ?? "").toLowerCase();
    if (!usernameValid || username.toLowerCase() === current) { setAvailability_("idle"); return; }
    setAvailability_("checking");
    let cancelled = false;
    const id = setTimeout(async () => {
      const { data, error } = await supabase.rpc("is_portfolio_username_available", { _username: username.toLowerCase() });
      if (cancelled) return;
      setAvailability_(error ? "idle" : data ? "free" : "taken");
    }, 400);
    return () => { cancelled = true; clearTimeout(id); };
  }, [username, usernameValid, initial?.portfolio_username]);

  async function save() {
    if (!usernameValid) return toast.error(t("Username: 3–32 lowercase letters, numbers, - or _", "اسم المستخدم: 3-32 حرفًا صغيرًا، أرقام، - أو _"));
    if (availability_ === "taken") return toast.error(t("That username is already taken.", "اسم المستخدم هذا مستخدم بالفعل."));
    setSaving(true);
    const skills = skillsText.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 24);
    const languages = languagesText.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 8).map((code: string) => ({ code }));
    const patch: any = {
      portfolio_username: username.toLowerCase(),
      portfolio_headline: headline || null,
      portfolio_bio: bio || null,
      portfolio_bio_long: bioLong || null,
      portfolio_skills: skills,
      portfolio_cover_url: cover || null,
      portfolio_country_code: country ? country.toUpperCase().slice(0, 2) : null,
      portfolio_languages: languages,
      portfolio_hourly_rate_usd: hourly ? Number(hourly) : null,
      portfolio_starting_price_usd: starting ? Number(starting) : null,
      portfolio_availability: availability,
      portfolio_response_hours: responseHours ? Number(responseHours) : null,
    };
    const { error } = await supabase.from("profiles").update(patch).eq("id", userId);
    setSaving(false);
    if (error) {
      const msg = error.message.includes("duplicate") || error.message.includes("profiles_portfolio_username") || (error as any).code === "23505"
        ? t("That username is already taken.", "اسم المستخدم هذا مستخدم بالفعل.") : error.message;
      return toast.error(msg);
    }
    toast.success(t("Profile updated", "تم تحديث الملف"));
    if (!initial?.portfolio_username) {
      import("@/lib/posthog").then(({ capturePostHog, PHEvents }) =>
        capturePostHog(PHEvents.PortfolioCreated, { username: username.toLowerCase() }),
      ).catch(() => {});
    }
    await onSaved();
  }

  return (
    <Card className="overflow-hidden border-border/60">
      <CardContent className="p-6 md:p-8 space-y-8">
        <section className="space-y-2">
          <div>
            <Label className="text-base font-semibold">{t("Cover banner", "غلاف الملف")}</Label>
            <p className="text-xs text-muted-foreground">{t("Wide image or short intro video (21:9 recommended).", "صورة عريضة أو فيديو تعريفي قصير (21:9 مُفضّل).")}</p>
          </div>
          <MediaUploader userId={userId} value={cover} onChange={setCover} aspect="cover"
            label={t("Add a cover image or intro video", "أضف صورة غلاف أو فيديو تعريفي")}
            hint={t("Recommended 21:9 · up to 50 MB", "مُفضّل 21:9 · حتى 50 ميغابايت")} />
        </section>

        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <Label htmlFor="pf-username" className="text-sm font-semibold">{t("Public URL", "الرابط العام")}</Label>
            <div className="mt-1.5 flex items-stretch overflow-hidden rounded-xl border border-border/70 bg-background focus-within:ring-2 focus-within:ring-primary/40">
              <span className="flex items-center bg-muted/60 px-3 text-xs text-muted-foreground border-e border-border/70">lumvio.co/portfolio/</span>
              <Input id="pf-username" value={username} maxLength={32}
                     onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                     placeholder={t("yourname", "اسمك")} className="border-0 focus-visible:ring-0" />
            </div>
            <p className={`mt-1 text-[11px] ${
              availability_ === "taken" ? "text-destructive" : usernameValid ? "text-emerald-500" : "text-muted-foreground"
            }`}>
              {!usernameValid
                ? t("3–32 characters. Lowercase, numbers, - or _", "3-32 حرفًا. أحرف صغيرة، أرقام، - أو _")
                : availability_ === "checking"
                  ? t("Checking availability…", "جارٍ التحقق من التوفر…")
                  : availability_ === "taken"
                    ? t("✕ That username is already taken.", "✕ اسم المستخدم هذا مستخدم بالفعل.")
                    : availability_ === "free"
                      ? t("✓ Username is available", "✓ اسم المستخدم متاح")
                      : t("✓ Available format", "✓ صيغة صحيحة")}
            </p>
          </div>

          <div>
            <Label htmlFor="pf-headline" className="text-sm font-semibold">{t("Professional headline", "العنوان المهني")}</Label>
            <Input id="pf-headline" value={headline} onChange={(e) => setHeadline(e.target.value)}
                   placeholder={t("Senior Brand & Motion Designer", "مصمم هوية وموشن جرافيك محترف")}
                   maxLength={140} className="mt-1.5 h-11" />
            <p className="mt-1 text-[11px] text-muted-foreground">{headline.length}/140</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <Label className="text-sm font-semibold inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {t("Country", "البلد")}</Label>
            <Input value={country} maxLength={2} placeholder="DZ" className="mt-1.5 h-11 uppercase"
                   onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0,2))} />
            <p className="mt-1 text-[10px] text-muted-foreground">{t("2-letter code (US, GB, DZ…)", "رمز مكوّن من حرفين (US، GB، DZ…)")}</p>
          </div>
          <div>
            <Label className="text-sm font-semibold inline-flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> {t("Hourly rate (USD)", "سعر الساعة (دولار)")}</Label>
            <Input value={hourly} type="number" placeholder="45" className="mt-1.5 h-11"
                   onChange={(e) => setHourly(e.target.value)} />
          </div>
          <div>
            <Label className="text-sm font-semibold inline-flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> {t("Starts at (USD)", "يبدأ من (دولار)")}</Label>
            <Input value={starting} type="number" placeholder="150" className="mt-1.5 h-11"
                   onChange={(e) => setStarting(e.target.value)} />
          </div>
          <div>
            <Label className="text-sm font-semibold inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t("Response time (h)", "وقت الرد (ساعات)")}</Label>
            <Input value={responseHours} type="number" placeholder="2" className="mt-1.5 h-11"
                   onChange={(e) => setResponseHours(e.target.value)} />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-sm font-semibold">{t("Availability", "الإتاحة")}</Label>
            <Select value={availability} onValueChange={setAvailability}>
              <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="available">🟢 {t("Available now", "متاح الآن")}</SelectItem>
                <SelectItem value="open">🟡 {t("Open to work", "مستعد للعمل")}</SelectItem>
                <SelectItem value="busy">🟠 {t("Busy but open", "مشغول لكن متاح")}</SelectItem>
                <SelectItem value="unavailable">⚪ {t("Not accepting", "لا يقبل عملًا")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">{t("Languages spoken", "اللغات التي تتحدثها")}</Label>
            <Input value={languagesText} onChange={(e) => setLanguagesText(e.target.value)}
                   placeholder={t("English, French, Arabic", "الإنجليزية، الفرنسية، العربية")} className="mt-1.5 h-11" />
          </div>
        </div>

        <div>
          <Label htmlFor="pf-bio" className="text-sm font-semibold">{t("Short pitch (shows in the header)", "نبذة قصيرة (تظهر في الترويسة)")}</Label>
          <Textarea id="pf-bio" value={bio} onChange={(e) => setBio(e.target.value)}
                    placeholder={t("One-liner about what you do and who you help.", "سطر واحد يوضّح ماذا تقدّم ولمن تعمل.")}
                    rows={2} maxLength={280} className="mt-1.5 leading-relaxed" />
          <p className="mt-1 text-[11px] text-muted-foreground">{bio.length}/280</p>
        </div>

        <div>
          <Label htmlFor="pf-bio-long" className="text-sm font-semibold">{t("About you (long)", "عنك (نصّ مطوّل)")}</Label>
          <Textarea id="pf-bio-long" value={bioLong} onChange={(e) => setBioLong(e.target.value)}
                    placeholder={t("Tell clients your background, working style, past clients, and outcomes.", "أخبر العملاء بخلفيتك، أسلوب عملك، عملاءك السابقين، والنتائج التي حقّقتها.")}
                    rows={7} maxLength={3000} className="mt-1.5 leading-relaxed" />
          <p className="mt-1 text-[11px] text-muted-foreground">{bioLong.length}/3000</p>
        </div>

        <div>
          <Label htmlFor="pf-skills" className="text-sm font-semibold">{t("Skills", "المهارات")}</Label>
          <Input id="pf-skills" value={skillsText} onChange={(e) => setSkillsText(e.target.value)}
                 placeholder={t("Figma, Webflow, Brand identity, Motion", "Figma، Webflow، هوية بصرية، موشن")} className="mt-1.5 h-11" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {skillsText.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 24).map((s: string) => (
              <Badge key={s} variant="secondary" className="rounded-full">{s}</Badge>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-border/60">
          <Button onClick={save} disabled={saving} size="lg" className="gap-2 rounded-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t("Save profile", "حفظ الملف")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Projects editor ----------
function ProjectsEditor({ userId, items, onChanged }: { userId: string; items: ProjectRow[]; onChanged: () => void }) {
  const t = useT();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectRow | null>(null);

  function openNew() { setEditing(null); setDialogOpen(true); }
  function openEdit(p: ProjectRow) { setEditing(p); setDialogOpen(true); }

  async function reorder(nextIds: string[]) {
    const { error } = await (supabase as any).rpc("reorder_portfolio", { _kind: "projects", _ids: nextIds });
    if (error) return toast.error(error.message);
    onChanged();
  }
  async function remove(id: string) {
    if (!confirm(t("Delete this project?", "حذف هذا المشروع؟"))) return;
    const { error } = await supabase.from("portfolio_projects").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(t("Deleted", "تم الحذف")); onChanged();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("Portfolio", "معرض الأعمال")}</h2>
          <p className="text-sm text-muted-foreground">{t("Drag to reorder. Photos and videos convert best.", "اسحب لإعادة الترتيب. الصور والفيديوهات تحقّق أفضل نتائج.")}</p>
        </div>
        <Button onClick={openNew} className="rounded-full gap-2"><Plus className="h-4 w-4" /> {t("Add project", "إضافة مشروع")}</Button>
      </div>

      {items.length === 0 ? (
        <EmptyBlock icon={<Briefcase className="h-6 w-6" />} title={t("No projects yet", "لا توجد مشاريع بعد")}
          body={t("Show 3+ pieces of your best work. Add a cover, a short story, tools you used, and the outcome.", "اعرض 3 أعمال أو أكثر. أضف صورة غلاف، وصفًا قصيرًا، الأدوات المستخدمة، والنتيجة.")}
          cta={<Button onClick={openNew} className="rounded-full gap-2"><Plus className="h-4 w-4" /> {t("Add first project", "أضف أول مشروع")}</Button>} />
      ) : (
        <SortableGrid
          items={items}
          onReorder={reorder}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          render={(p, hp) => (
            <div className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card transition-all hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_color-mix(in_oklab,var(--primary)_40%,transparent)]">
              <div className="relative aspect-video overflow-hidden bg-muted">
                {p.cover_url ? (
                  isVideoUrl(p.cover_url) ? (
                    <video src={p.cover_url} muted playsInline loop
                           onMouseEnter={(e) => (e.currentTarget as HTMLVideoElement).play()}
                           onMouseLeave={(e) => (e.currentTarget as HTMLVideoElement).pause()}
                           className="h-full w-full object-cover" />
                  ) : (
                    <img src={p.cover_url} alt={p.title} loading="lazy"
                         className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  )
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-4xl opacity-20">✦</div>
                )}
                {p.cover_url && isVideoUrl(p.cover_url) && (
                  <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur inline-flex items-center gap-1">
                    <Film className="h-3 w-3" /> {t("Video", "فيديو")}
                  </span>
                )}
                <div className="absolute right-2 top-2 opacity-0 transition group-hover:opacity-100">
                  <DragHandle attributes={hp.attributes} listeners={hp.listeners} />
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold leading-tight">{p.title}</h3>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => openEdit(p)} aria-label={t("Edit", "تعديل")}>
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(p.id)} aria-label={t("Delete", "حذف")}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {p.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.description}</p>}
                {p.tags?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.tags.slice(0, 4).map((tg) => <Badge key={tg} variant="secondary" className="text-[10px] rounded-full">{tg}</Badge>)}
                  </div>
                )}
              </div>
            </div>
          )}
        />
      )}

      <ProjectDialog open={dialogOpen} onOpenChange={setDialogOpen} userId={userId} editing={editing}
        nextOrder={items.length + 1} onSaved={() => { setDialogOpen(false); onChanged(); }} />
    </div>
  );
}

function ProjectDialog({
  open, onOpenChange, userId, editing, nextOrder, onSaved,
}: { open: boolean; onOpenChange: (b: boolean) => void; userId: string; editing: ProjectRow | null; nextOrder: number; onSaved: () => void }) {
  const t = useT();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [role, setRole] = useState("");
  const [tools, setTools] = useState("");
  const [outcome, setOutcome] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [external, setExternal] = useState("");
  const [cover, setCover] = useState<string | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);
  const [galleryDraft, setGalleryDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title ?? "");
    setDescription(editing?.description ?? "");
    setRole(editing?.role ?? "");
    setTools((editing?.tools ?? []).join(", "));
    setOutcome(editing?.outcome ?? "");
    setCategory(editing?.category ?? "");
    setTags((editing?.tags ?? []).join(", "));
    setExternal(editing?.external_url ?? "");
    setCover(editing?.cover_url ?? null);
    setGallery(editing?.gallery ?? []);
    setGalleryDraft(null);
  }, [open, editing]);

  useEffect(() => {
    if (galleryDraft) {
      setGallery((g) => [...g, galleryDraft]);
      setGalleryDraft(null);
    }
  }, [galleryDraft]);

  async function save() {
    if (!title.trim()) return toast.error(t("Title is required", "العنوان مطلوب"));
    setSaving(true);
    const payload: any = {
      user_id: userId,
      title,
      description: description || null,
      role: role || null,
      tools: tools.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 12),
      outcome: outcome || null,
      category: category || null,
      cover_url: cover,
      external_url: external || null,
      tags: tags.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 8),
      gallery,
    };
    if (editing) {
      const { error } = await supabase.from("portfolio_projects").update(payload).eq("id", editing.id);
      if (error) { setSaving(false); return toast.error(error.message); }
    } else {
      payload.sort_order = nextOrder;
      const { error } = await supabase.from("portfolio_projects").insert(payload);
      if (error) { setSaving(false); return toast.error(error.message); }
    }
    setSaving(false);
    toast.success(editing ? t("Updated", "تم التحديث") : t("Added", "تمت الإضافة"));
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? t("Edit project", "تعديل المشروع") : t("Add project", "إضافة مشروع")}</DialogTitle></DialogHeader>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-3">
            <Label className="text-sm font-semibold">{t("Cover", "الغلاف")}</Label>
            <MediaUploader userId={userId} value={cover} onChange={setCover} aspect="video" />

            <Label className="text-sm font-semibold pt-2">{t("Extra gallery items", "عناصر معرض إضافية")} ({gallery.length})</Label>
            {gallery.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {gallery.map((url, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-lg border border-border/60">
                    {isVideoUrl(url) ? <video src={url} className="h-full w-full object-cover" muted /> : <img src={url} className="h-full w-full object-cover" />}
                    <button onClick={() => setGallery((g) => g.filter((_, j) => j !== i))} className="absolute top-1 right-1 rounded-full bg-black/70 p-1 text-white">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <MediaUploader userId={userId} value={null} onChange={(u) => u && setGalleryDraft(u)} compact aspect="square" label={t("Add gallery item", "أضف عنصر معرض")} hint={t("Image or video", "صورة أو فيديو")} />
          </div>

          <div className="space-y-3">
            <div><Label className="text-sm font-semibold">{t("Title", "العنوان")}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 h-10" placeholder={t("e.g. Rebranded a fintech SaaS", "مثال: إعادة تصميم هوية شركة تقنية مالية")} /></div>
            <div><Label className="text-sm font-semibold">{t("Category", "التصنيف")}</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1.5 h-10" placeholder={t("Branding · Motion · UI…", "هوية · موشن · واجهة…")} /></div>
            <div><Label className="text-sm font-semibold">{t("Short description", "وصف قصير")}</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder={t("Goal, deliverables, outcome", "الهدف، المخرجات، النتيجة")} /></div>
            <div><Label className="text-sm font-semibold">{t("Your role", "دورك في المشروع")}</Label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} className="mt-1.5 h-10" placeholder={t("Lead designer · Sole developer", "مصمم رئيسي · مطوّر منفرد")} /></div>
            <div><Label className="text-sm font-semibold">{t("Tools used", "الأدوات المستخدمة")}</Label>
              <Input value={tools} onChange={(e) => setTools(e.target.value)} className="mt-1.5 h-10" placeholder="Figma, After Effects, Webflow" /></div>
            <div><Label className="text-sm font-semibold">{t("Outcome", "النتيجة")}</Label>
              <Input value={outcome} onChange={(e) => setOutcome(e.target.value)} className="mt-1.5 h-10" placeholder={t("e.g. +45% signup lift", "مثال: زيادة التسجيل بـ 45%")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">{t("Live URL", "الرابط المباشر")}</Label>
                <Input value={external} onChange={(e) => setExternal(e.target.value)} className="mt-1.5" placeholder="https://…" /></div>
              <div><Label className="text-sm font-semibold">{t("Tags", "الوسوم")}</Label>
                <Input value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1.5" placeholder={t("branding, motion", "هوية، موشن")} /></div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border/60">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("Cancel", "إلغاء")}</Button>
          <Button onClick={save} disabled={saving} className="rounded-full gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {editing ? t("Save changes", "حفظ التغييرات") : t("Add project", "إضافة المشروع")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Services editor ----------
function ServicesEditor({ userId, items, onChanged }: { userId: string; items: ServiceRow[]; onChanged: () => void }) {
  const t = useT();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceRow | null>(null);
  function openNew() { setEditing(null); setDialogOpen(true); }
  function openEdit(s: ServiceRow) { setEditing(s); setDialogOpen(true); }
  async function reorder(nextIds: string[]) {
    const { error } = await (supabase as any).rpc("reorder_portfolio", { _kind: "services", _ids: nextIds });
    if (error) return toast.error(error.message);
    onChanged();
  }
  async function remove(id: string) {
    if (!confirm(t("Delete this service?", "حذف هذه الخدمة؟"))) return;
    const { error } = await supabase.from("portfolio_services").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(t("Deleted", "تم الحذف")); onChanged();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("Services & Packages", "الخدمات والباقات")}</h2>
          <p className="text-sm text-muted-foreground">{t("Productize what you sell. Add tiered packages for higher conversions.", "قدّم ما تبيعه كمنتج. أضف باقات متعدّدة لزيادة التحويل.")}</p>
        </div>
        <Button onClick={openNew} className="rounded-full gap-2"><Plus className="h-4 w-4" /> {t("Add service", "إضافة خدمة")}</Button>
      </div>

      {items.length === 0 ? (
        <EmptyBlock icon={<Package className="h-6 w-6" />} title={t("No services published", "لا توجد خدمات منشورة")}
          body={t("Clients pick services faster than open briefs. Add one service with a starting price and delivery time.", "العملاء يختارون الخدمات أسرع من الطلبات المفتوحة. أضف خدمة بسعر ابتدائي ومدة تسليم.")}
          cta={<Button onClick={openNew} className="rounded-full gap-2"><Plus className="h-4 w-4" /> {t("Add first service", "أضف أول خدمة")}</Button>} />
      ) : (
        <SortableGrid
          items={items}
          onReorder={reorder}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          render={(s, hp) => (
            <Card className="group relative overflow-hidden border-border/60 bg-card/60 backdrop-blur-xl">
              <div className="absolute right-2 top-2 opacity-0 transition group-hover:opacity-100">
                <DragHandle attributes={hp.attributes} listeners={hp.listeners} />
              </div>
              {s.thumbnail_url && (
                <div className="aspect-video overflow-hidden bg-muted">
                  <img src={s.thumbnail_url} className="h-full w-full object-cover" />
                </div>
              )}
              <CardContent className="p-5">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">{s.category || t("Service", "خدمة")}</div>
                <h3 className="mt-1 text-base font-semibold">{s.title}</h3>
                {s.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{s.description}</p>}

                {s.tiers?.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {s.tiers.map((tr, i) => (
                      <Badge key={i} variant={tr.recommended ? "default" : "secondary"} className="rounded-full text-[10px]">
                        {tr.name} · ${tr.price}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 flex items-end justify-between">
                    <div>
                      {s.price_from != null && <div className="text-xl font-semibold">${s.price_from}<span className="text-xs font-normal text-muted-foreground"> {t("from", "بدءًا من")}</span></div>}
                      {s.delivery_days != null && <div className="text-xs text-muted-foreground">{t("Delivery in", "تسليم خلال")} {s.delivery_days}{t("d", " يوم")}</div>}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)}>{t("Edit", "تعديل")}</Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(s.id)} aria-label={t("Delete", "حذف")}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        />
      )}

      <ServiceDialog open={dialogOpen} onOpenChange={setDialogOpen} userId={userId} editing={editing}
        nextOrder={items.length + 1} onSaved={() => { setDialogOpen(false); onChanged(); }} />
    </div>
  );
}

function ServiceDialog({
  open, onOpenChange, userId, editing, nextOrder, onSaved,
}: { open: boolean; onOpenChange: (b: boolean) => void; userId: string; editing: ServiceRow | null; nextOrder: number; onSaved: () => void }) {
  const t = useT();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [priceFrom, setPriceFrom] = useState<string>("");
  const [deliveryDays, setDeliveryDays] = useState<string>("");
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [useTiers, setUseTiers] = useState(false);
  const [tiers, setTiers] = useState<Tier[]>([
    { name: "Basic", price: 100, delivery_days: 3, features: [""] },
    { name: "Standard", price: 250, delivery_days: 7, features: [""], recommended: true },
    { name: "Premium", price: 500, delivery_days: 14, features: [""] },
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title ?? "");
    setDescription(editing?.description ?? "");
    setCategory(editing?.category ?? "");
    setPriceFrom(editing?.price_from ? String(editing.price_from) : "");
    setDeliveryDays(editing?.delivery_days ? String(editing.delivery_days) : "");
    setThumbnail(editing?.thumbnail_url ?? null);
    setUseTiers((editing?.tiers?.length ?? 0) > 0);
    if (editing?.tiers?.length) setTiers(editing.tiers);
  }, [open, editing]);

  async function save() {
    if (!title.trim()) return toast.error(t("Title is required", "العنوان مطلوب"));
    setSaving(true);
    const cleanTiers = useTiers
      ? tiers.map((tr) => ({ ...tr, features: (tr.features || []).map((f) => f.trim()).filter(Boolean) }))
      : [];
    const payload: any = {
      user_id: userId,
      title,
      description: description || null,
      category: category || null,
      price_from: priceFrom ? Number(priceFrom) : (cleanTiers[0]?.price ?? null),
      delivery_days: deliveryDays ? Number(deliveryDays) : (cleanTiers[0]?.delivery_days ?? null),
      thumbnail_url: thumbnail,
      tiers: cleanTiers,
    };
    if (editing) {
      const { error } = await supabase.from("portfolio_services").update(payload).eq("id", editing.id);
      if (error) { setSaving(false); return toast.error(error.message); }
    } else {
      payload.sort_order = nextOrder;
      const { error } = await supabase.from("portfolio_services").insert(payload);
      if (error) { setSaving(false); return toast.error(error.message); }
    }
    setSaving(false);
    toast.success(editing ? t("Updated", "تم التحديث") : t("Added", "تمت الإضافة"));
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? t("Edit service", "تعديل الخدمة") : t("Add service", "إضافة خدمة")}</DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <div><Label className="text-sm font-semibold">{t("Title", "العنوان")}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 h-10" placeholder={t("e.g. Landing page in 5 days", "مثال: صفحة هبوط خلال 5 أيام")} /></div>
            <div><Label className="text-sm font-semibold">{t("Category", "التصنيف")}</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1.5 h-10" placeholder={t("Web design · Motion · Branding", "تصميم مواقع · موشن · هوية")} /></div>
            <div><Label className="text-sm font-semibold">{t("Description", "الوصف")}</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder={t("What's included, who it's for, why it's a fit.", "ما الذي تتضمّنه، لمن هي، ولماذا مناسبة.")} /></div>
            <div><Label className="text-sm font-semibold">{t("Thumbnail", "الصورة المصغّرة")}</Label>
              <MediaUploader userId={userId} value={thumbnail} onChange={setThumbnail} aspect="video" compact /></div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
              <div>
                <div className="text-sm font-semibold">{t("Tiered packages", "باقات متعدّدة")}</div>
                <p className="text-xs text-muted-foreground">{t("Basic · Standard · Premium", "أساسية · قياسية · مميّزة")}</p>
              </div>
              <Switch checked={useTiers} onCheckedChange={setUseTiers} />
            </div>

            {!useTiers ? (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm font-semibold">{t("Price from ($)", "السعر بدءًا من ($)")}</Label>
                  <Input value={priceFrom} type="number" onChange={(e) => setPriceFrom(e.target.value)} className="mt-1.5 h-10" placeholder="150" /></div>
                <div><Label className="text-sm font-semibold">{t("Delivery (days)", "التسليم (أيام)")}</Label>
                  <Input value={deliveryDays} type="number" onChange={(e) => setDeliveryDays(e.target.value)} className="mt-1.5 h-10" placeholder="5" /></div>
              </div>
            ) : (
              <div className="space-y-3">
                {tiers.map((tr, idx) => (
                  <div key={idx} className={`rounded-xl border p-3 space-y-2 ${tr.recommended ? "border-primary/50 bg-primary/5" : "border-border/60"}`}>
                    <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                      <Input value={tr.name} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} className="h-9" />
                      <label className="text-[11px] inline-flex items-center gap-1.5">
                        <Switch checked={!!tr.recommended} onCheckedChange={(v) => setTiers(tiers.map((x, i) => ({ ...x, recommended: i === idx ? v : (v ? false : x.recommended) })))} /> {t("Recommended", "موصى بها")}
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" value={tr.price} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, price: Number(e.target.value) } : x))} className="h-9" placeholder={t("Price $", "السعر $")} />
                      <Input type="number" value={tr.delivery_days} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, delivery_days: Number(e.target.value) } : x))} className="h-9" placeholder={t("Days", "أيام")} />
                    </div>
                    <Textarea value={(tr.features || []).join("\n")}
                      onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, features: e.target.value.split("\n") } : x))}
                      rows={3} className="text-xs" placeholder={t("One feature per line", "ميزة واحدة في كل سطر")} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border/60">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("Cancel", "إلغاء")}</Button>
          <Button onClick={save} disabled={saving} className="rounded-full gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {editing ? t("Save changes", "حفظ التغييرات") : t("Add service", "إضافة الخدمة")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Experience editor ----------
function ExperienceEditor({ userId, items, onChanged }: { userId: string; items: ExperienceRow[]; onChanged: () => void }) {
  const t = useT();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExperienceRow | null>(null);

  async function reorder(nextIds: string[]) {
    const { error } = await (supabase as any).rpc("reorder_portfolio", { _kind: "experience", _ids: nextIds });
    if (error) return toast.error(error.message);
    onChanged();
  }
  async function remove(id: string) {
    if (!confirm(t("Delete this entry?", "حذف هذا العنصر؟"))) return;
    const { error } = await (supabase as any).from("portfolio_experience").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(t("Deleted", "تم الحذف")); onChanged();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("Experience & Certifications", "الخبرة والشهادات")}</h2>
          <p className="text-sm text-muted-foreground">{t("Optional. Boosts trust with agencies and enterprise clients.", "اختياري. يعزّز الثقة مع الوكالات والعملاء الكبار.")}</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="rounded-full gap-2"><Plus className="h-4 w-4" /> {t("Add entry", "إضافة عنصر")}</Button>
      </div>

      {items.length === 0 ? (
        <EmptyBlock icon={<Award className="h-6 w-6" />} title={t("No experience yet", "لا توجد خبرات بعد")}
          body={t("Add employment, education, or certifications to build authority.", "أضف وظائف، دراسات، أو شهادات لبناء المصداقية.")}
          cta={<Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="rounded-full gap-2"><Plus className="h-4 w-4" /> {t("Add first entry", "أضف أول عنصر")}</Button>} />
      ) : (
        <SortableGrid
          items={items}
          onReorder={reorder}
          className="grid gap-3"
          render={(x, hp) => (
            <div className="group flex gap-3 rounded-2xl border border-border/60 bg-card/60 p-4">
              <DragHandle attributes={hp.attributes} listeners={hp.listeners} />
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                {x.kind === "employment" ? <Building2 className="h-4 w-4" /> : x.kind === "education" ? <GraduationCap className="h-4 w-4" /> : <Trophy className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold">{x.title}</div>
                    <div className="text-xs text-muted-foreground">{x.org} · {formatRange(x.start_date, x.end_date, t)}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(x); setDialogOpen(true); }}>{t("Edit", "تعديل")}</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(x.id)} aria-label={t("Delete", "حذف")}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {x.description && <p className="mt-1 text-sm text-muted-foreground">{x.description}</p>}
              </div>
            </div>
          )}
        />
      )}

      <ExperienceDialog open={dialogOpen} onOpenChange={setDialogOpen} userId={userId} editing={editing}
        nextOrder={items.length + 1} onSaved={() => { setDialogOpen(false); onChanged(); }} />
    </div>
  );
}

function ExperienceDialog({
  open, onOpenChange, userId, editing, nextOrder, onSaved,
}: { open: boolean; onOpenChange: (b: boolean) => void; userId: string; editing: ExperienceRow | null; nextOrder: number; onSaved: () => void }) {
  const t = useT();
  const [kind, setKind] = useState<ExperienceRow["kind"]>("employment");
  const [title, setTitle] = useState("");
  const [org, setOrg] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [description, setDescription] = useState("");
  const [credential, setCredential] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind(editing?.kind ?? "employment");
    setTitle(editing?.title ?? "");
    setOrg(editing?.org ?? "");
    setStart(editing?.start_date ?? "");
    setEnd(editing?.end_date ?? "");
    setDescription(editing?.description ?? "");
    setCredential(editing?.credential_url ?? "");
  }, [open, editing]);

  async function save() {
    if (!title.trim()) return toast.error(t("Title is required", "العنوان مطلوب"));
    setSaving(true);
    const payload: any = {
      user_id: userId,
      kind,
      title,
      org: org || null,
      start_date: start || null,
      end_date: end || null,
      description: description || null,
      credential_url: credential || null,
    };
    if (editing) {
      const { error } = await (supabase as any).from("portfolio_experience").update(payload).eq("id", editing.id);
      if (error) { setSaving(false); return toast.error(error.message); }
    } else {
      payload.sort_order = nextOrder;
      const { error } = await (supabase as any).from("portfolio_experience").insert(payload);
      if (error) { setSaving(false); return toast.error(error.message); }
    }
    setSaving(false);
    toast.success(editing ? t("Updated", "تم التحديث") : t("Added", "تمت الإضافة")); onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? t("Edit entry", "تعديل العنصر") : t("Add entry", "إضافة عنصر")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm font-semibold">{t("Type", "النوع")}</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as any)}>
              <SelectTrigger className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="employment">{t("Employment", "عمل")}</SelectItem>
                <SelectItem value="education">{t("Education", "تعليم")}</SelectItem>
                <SelectItem value="certification">{t("Certification", "شهادة")}</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label className="text-sm font-semibold">{t("Title / role", "العنوان / المسمّى")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 h-10" placeholder={t("Senior Designer", "مصمم أول")} /></div>
          <div><Label className="text-sm font-semibold">{t("Organization", "الجهة")}</Label>
            <Input value={org} onChange={(e) => setOrg(e.target.value)} className="mt-1.5 h-10" placeholder={t("Company / school", "شركة / مدرسة")} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-sm font-semibold">{t("Start", "البداية")}</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5 h-10" /></div>
            <div><Label className="text-sm font-semibold">{t("End (leave empty = present)", "النهاية (اتركها فارغة = حتى الآن)")}</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5 h-10" /></div>
          </div>
          <div><Label className="text-sm font-semibold">{t("Description", "الوصف")}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} /></div>
          {kind === "certification" && (
            <div><Label className="text-sm font-semibold">{t("Credential URL", "رابط الشهادة")}</Label>
              <Input value={credential} onChange={(e) => setCredential(e.target.value)} className="mt-1.5 h-10" placeholder="https://…" /></div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border/60">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("Cancel", "إلغاء")}</Button>
          <Button onClick={save} disabled={saving} className="rounded-full gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("Save", "حفظ")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reviews panel (read-only summary) ----------
function ReviewsPanel({ items }: { items: TestimonialRow[] }) {
  const t = useT();
  const total = items.length;
  const avg = total ? items.reduce((s, x) => s + (x.rating ?? 0), 0) / total : 0;
  const breakdown = [5, 4, 3, 2, 1].map((r) => ({ r, c: items.filter((x) => (x.rating ?? 0) === r).length }));

  if (total === 0) {
    return (
      <EmptyBlock icon={<Star className="h-6 w-6" />} title={t("No reviews yet", "لا توجد تقييمات بعد")}
        body={t("Reviews come from clients after they complete a project with you. Once you deliver great work, they can leave a rating and comment on your public profile.",
               "التقييمات تصل من العملاء بعد إنهاء مشروع معك. عندما تقدّم عملًا رائعًا، يمكنهم ترك تقييم وتعليق على ملفك العام.")} />
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-border/60">
        <CardContent className="p-6 grid gap-6 md:grid-cols-[auto_1fr]">
          <div className="text-center">
            <div className="text-5xl font-semibold tabular-nums">{avg.toFixed(1)}</div>
            <div className="mt-1 flex justify-center gap-0.5 text-amber-500">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < Math.round(avg) ? "fill-current" : "opacity-30"}`} />
              ))}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{total} {t(total !== 1 ? "reviews" : "review", "تقييم")}</div>
          </div>
          <div className="space-y-1.5">
            {breakdown.map(({ r, c }) => (
              <div key={r} className="flex items-center gap-2 text-xs">
                <span className="w-6 tabular-nums">{r}★</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-amber-500 transition-all" style={{ width: `${total ? (c / total) * 100 : 0}%` }} />
                </div>
                <span className="w-8 text-end tabular-nums text-muted-foreground">{c}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map((tm) => (
          <div key={tm.id} className="rounded-2xl border border-border/60 bg-card/60 p-5">
            {tm.rating != null && (
              <div className="mb-2 flex gap-0.5 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-3.5 w-3.5 ${i < tm.rating! ? "fill-current" : "opacity-25"}`} />
                ))}
              </div>
            )}
            <blockquote className="text-sm leading-relaxed">"{tm.quote}"</blockquote>
            <div className="mt-3 text-xs">
              <div className="font-semibold">{tm.client_name}</div>
              {tm.client_role && <div className="text-muted-foreground">{tm.client_role}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- helpers ----------
function normalizeProject(row: any): ProjectRow {
  return {
    ...row,
    tags: row.tags ?? [],
    tools: Array.isArray(row.tools) ? row.tools : [],
    gallery: Array.isArray(row.gallery) ? row.gallery : [],
    sort_order: row.sort_order ?? 0,
  };
}
function normalizeService(row: any): ServiceRow {
  return {
    ...row,
    tiers: Array.isArray(row.tiers) ? row.tiers : [],
    sort_order: row.sort_order ?? 0,
  };
}
function formatRange(a: string | null, b: string | null, t: (en: string, ar: string) => string) {
  const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : t("Present", "حتى الآن");
  if (!a && !b) return "";
  return `${fmt(a)} – ${fmt(b)}`;
}
